import apiClient from './apiClient';
import { portfolioAuthHeaders } from '../components/LandingPages/Portfolio/usePortfolioUser';
import { getApiErrorMessage } from '../utils/apiErrorUtils';

const BASE_URL = process.env.REACT_APP_BASE_URL || '';

/** ISO 6166 ISIN — matches SimplyBond backend validation. */
export const ISIN_PATTERN = /^[A-Z]{2}[A-Z0-9]{9}[0-9]$/;

const CENTRAL_GOV_CATEGORIES = new Set(['G-Sec', 'T-Bill', 'Sovereign Gold Bond']);
const STATE_GOV_CATEGORIES = new Set(['SDL']);

/** Map engine risk profile → existing SimplyBond portfolio strategy type. */
export function portfolioTypeFromResponse(portfolioResponse) {
  const risk = portfolioResponse?.request?.risk_profile;
  const map = {
    conservative: 'PRINCIPAL_PROTECTION',
    moderate: 'REGULAR_INCOME',
    balanced: 'TARGET_RETURN_PORTFOLIO',
    growth: 'MODIFIED_RISK',
    aggressive: 'HIGH_RETURN',
  };
  return map[risk] || 'REGULAR_INCOME';
}

/**
 * Backend stores totalMarketValue = price × quantity × 10.
 * With quantity = 1, set price so the stored market value equals engine amount_inr.
 */
export function priceFromEngineAmount(amountInr) {
  const amount = Number(amountInr) || 0;
  return Number((amount / 10).toFixed(4));
}

export function isSavableIsin(isin) {
  return typeof isin === 'string' && ISIN_PATTERN.test(isin.trim().toUpperCase());
}

function parseCouponFromName(name) {
  if (!name) return undefined;
  const match = name.match(/(\d+(?:\.\d+)?)\s*%/);
  return match ? Number(match[1]) : undefined;
}

/** G-Sec / SDL coupon from nomenclature — used when enriching RBI instrument details. */
function parseCouponFromNomenclature(nomenclature) {
  if (!nomenclature) return undefined;
  const firstPart = String(nomenclature).trim().split(/\s+/)[0];
  if (firstPart && firstPart.includes('%')) {
    const n = Number(firstPart.replace('%', ''));
    return Number.isFinite(n) ? n : undefined;
  }
  return parseCouponFromName(nomenclature);
}

function isCentralGovCategory(category) {
  return CENTRAL_GOV_CATEGORIES.has(category);
}

function isStateGovCategory(category) {
  return STATE_GOV_CATEGORIES.has(category);
}

/** Fallback when category missing — common India ISIN prefixes. */
function isLikelyCentralGovIsin(isin) {
  return /^IN0(0[0-9]|1[0-9]|2[0-9])/.test(isin);
}

function isLikelyStateGovIsin(isin) {
  return /^IN(10|12|24)/.test(isin);
}

/** Saved portfolio row — G-Sec / SDL (issuer label or India RBI ISIN prefix). */
export function isGovtPortfolioHolding(row) {
  if (!row) return false;
  const issuer = String(row.issuer || '').trim();
  if (issuer === 'Government of India') return true;
  const isin = String(row.isin || '').trim().toUpperCase();
  if (!isin) return false;
  return isLikelyCentralGovIsin(isin) || isLikelyStateGovIsin(isin);
}

function normalizeCentralGovRecord(entity) {
  if (!entity) return null;
  return {
    securityType: 'Central Government Securities',
    issuer: 'Government of India',
    issueDate: entity.dateOfIssue,
    maturityDate: entity.dateOfMaturity,
    nomenclature: entity.nomenclature,
    coupon: parseCouponFromNomenclature(entity.nomenclature),
    outstandingStock: entity.outstandingStock,
  };
}

function normalizeStateGovRecord(entity) {
  if (!entity) return null;
  const state = entity.stateGovernment || entity.companyIssuerName;
  return {
    securityType: 'State Government Securities',
    issuer: state || entity.nomenclature,
    stateGovernment: state,
    companyIssuerName: entity.companyIssuerName || state,
    issueDate: entity.dateOfIssue,
    maturityDate: entity.dateOfMaturity,
    nomenclature: entity.nomenclature,
    coupon: parseCouponFromNomenclature(entity.nomenclature),
    outstandingStock: entity.outstandingStock,
  };
}

function holdingToUpsertPayload(holding, details = null, portfolioType = 'REGULAR_INCOME') {
  const inst = holding.instrument;
  const isin = String(inst?.isin_prefix || '').trim().toUpperCase();
  const isGovtCentral = details?.securityType === 'Central Government Securities'
    || (!details && (isCentralGovCategory(inst?.category || '') || isLikelyCentralGovIsin(isin)));
  const isGovtState = details?.securityType === 'State Government Securities'
    || (!details && (isStateGovCategory(inst?.category || '') || isLikelyStateGovIsin(isin)));

  let issuer = inst.name;
  if (isGovtCentral) {
    issuer = 'Government of India';
  } else if (isGovtState) {
    issuer =
      details.stateGovernment ||
      details.companyIssuerName ||
      inst.name;
  } else if (details) {
    issuer =
      details.companyIssuerName ||
      details.issuerName ||
      details.issuer ||
      inst.name;
  }

  const maturityDate = isGovtCentral || isGovtState
    ? (details?.maturityDate || details?.dateOfMaturity)
    : (details?.redemption || details?.dateOfRedemption || details?.dateOfMaturity);

  const issueDate = isGovtCentral || isGovtState
    ? (details?.issueDate || details?.dateOfIssue)
    : (details?.allotmentDate || details?.issueDate || details?.dateOfIssue);

  const rawCoupon = isGovtCentral || isGovtState
    ? details?.coupon
    : (details?.couponRate ?? details?.coupon);

  return {
    quantity: 1,
    price: priceFromEngineAmount(holding.amount_inr),
    type: portfolioType,
    issuer,
    issueDate: issueDate || undefined,
    maturityDate: maturityDate || undefined,
    coupon: rawCoupon ?? undefined,
    yield: inst.indicative_yield ?? undefined,
  };
}

async function fetchRbiCentralByIsin(isin) {
  const res = await apiClient.get(`${BASE_URL}/rbi/g-sec/${encodeURIComponent(isin)}`, {
    validateStatus: () => true,
  });
  if (res.status === 200 && res.data?.data) {
    return normalizeCentralGovRecord(res.data.data);
  }
  return null;
}

async function fetchRbiStateByIsin(isin) {
  const res = await apiClient.get(`${BASE_URL}/rbi/state-g-sec/${encodeURIComponent(isin)}`, {
    validateStatus: () => true,
  });
  if (res.status === 200 && res.data?.data) {
    return normalizeStateGovRecord(res.data.data);
  }
  return null;
}

async function fetchMasterIsinDetails(isin) {
  const res = await apiClient.get(`${BASE_URL}/master-isin/details/${encodeURIComponent(isin)}`, {
    validateStatus: () => true,
  });
  if (res.status === 200 && res.data?.data) {
    return res.data.data;
  }
  return null;
}

/**
 * Resolve instrument metadata from the correct SimplyBond source:
 * - G-Sec / T-Bill / SGB → rbi_government_securities (`/rbi/g-sec/{isin}`)
 * - SDL → rbi_state_government_securities (`/rbi/state-g-sec/{isin}`)
 * - Corporate / PSU / NCD → master_isin (`/master-isin/details/{isin}`)
 *
 * Mirrors Principal Protection / PortfolioUniversePage behaviour.
 */
async function fetchInstrumentDetails(holding) {
  const isin = String(holding.instrument?.isin_prefix || '').trim().toUpperCase();
  const category = holding.instrument?.category || '';

  try {
    if (isCentralGovCategory(category) || isLikelyCentralGovIsin(isin)) {
      const central = await fetchRbiCentralByIsin(isin);
      if (central) return central;
    }

    if (isStateGovCategory(category) || isLikelyStateGovIsin(isin)) {
      const state = await fetchRbiStateByIsin(isin);
      if (state) return state;
    }

    // Category said central but not found — try state before master (edge cases)
    if (isCentralGovCategory(category)) {
      const state = await fetchRbiStateByIsin(isin);
      if (state) return state;
    }

    return await fetchMasterIsinDetails(isin);
  } catch (err) {
    console.warn('Instrument enrichment failed; saving with engine metadata only', isin, err);
    return null;
  }
}

/**
 * Persist engine holdings via POST /user/portfolio/{isin} (upsert), same as strategy pages.
 * Skips synthetic / non-ISIN instruments (MF, FD profile slots, small savings codes).
 */
export async function saveEnginePortfolioHoldings(portfolioResponse, userId, { onProgress } = {}) {
  if (!userId) {
    throw new Error('User must be logged in to save portfolio holdings');
  }

  const holdings = portfolioResponse?.holdings || [];
  const portfolioType = portfolioTypeFromResponse(portfolioResponse);
  const result = { saved: [], skipped: [], failed: [], portfolioType };
  const headers = portfolioAuthHeaders(userId);

  for (let i = 0; i < holdings.length; i += 1) {
    const holding = holdings[i];
    const isin = String(holding.instrument?.isin_prefix || '').trim().toUpperCase();
    const name = holding.instrument?.name || isin;

    if (onProgress) {
      onProgress({ index: i + 1, total: holdings.length, isin, name });
    }

    if (!isSavableIsin(isin)) {
      result.skipped.push({
        isin,
        name,
        reason: 'Synthetic or non-ISIN instrument (not stored in bond portfolio)',
      });
      continue;
    }

    try {
      const details = await fetchInstrumentDetails(holding);
      const payload = holdingToUpsertPayload(holding, details, portfolioType);
      await apiClient.post(`${BASE_URL}/user/portfolio/${isin}`, payload, {
        headers,
      });
      result.saved.push({
        isin,
        name,
        amount_inr: holding.amount_inr,
        source: details?.securityType ? 'rbi' : details ? 'master-isin' : 'engine-only',
      });
    } catch (err) {
      result.failed.push({ isin, name, error: getApiErrorMessage(err, 'Save failed') });
    }
  }

  return result;
}

export function summarizeSaveResult(result) {
  const parts = [];
  if (result.saved.length) parts.push(`${result.saved.length} saved`);
  if (result.skipped.length) parts.push(`${result.skipped.length} skipped (synthetic)`);
  if (result.failed.length) parts.push(`${result.failed.length} failed`);
  return parts.join(' · ') || 'Nothing to save';
}
